import React from 'react';
import clsx from 'clsx';

/**
 * Future-compose atoms (Iteration 12 — built + shown in the component showcase;
 * COMPOSED INTO SURFACES in Iteration 13, not wired into pages here).
 *
 * Building them now (as canon-conformant primitives) means Iter-13 composes
 * rather than re-invents. They follow the same token rules as the rest of ui/:
 * flat cards (shadow only on overlays), DS tokens, brand-action accents.
 */

/* ── EntityDetailCard ─────────────────────────────────────────────────────
 * Flat titled card for an entity's detail surface (header + optional actions
 * + body). Shadowless by canon (SYS-CARD-01).
 */
// PT-39b (2026-07-27): the duplicate EntityDetailCard shadow that lived here
// has been deleted. The canonical atom was ui/EntityDetailCard.js (itself now
// retired) — having two definitions with the same name and diverging prop
// shapes was a latent hazard flagged in PR-PT-39 spec §39.2. Consumers should
// use ui/GoldenCustomerCard.js.

/* ── StickyCostSummary ────────────────────────────────────────────────────
 * Sticky totals panel (e.g. quote / job costing). Sticks to top within its
 * scroll container; emphasises the grand total.
 */
export function StickyCostSummary({ lines = [], total, totalLabel = 'Total', footer, className }) {
  return (
    <aside
      className={clsx('sticky top-4 rounded-[var(--radius-lg)] border p-4', className)}
      style={{ background: 'var(--color-surface)', borderColor: 'var(--color-border)', boxShadow: 'none' }}
    >
      <dl className="space-y-1.5">
        {lines.map((l, i) => (
          <div key={i} className="flex items-center justify-between text-sm">
            <dt style={{ color: 'var(--color-text-muted)' }}>{l.label}</dt>
            <dd style={{ color: 'var(--color-text-default)' }}>{l.value}</dd>
          </div>
        ))}
      </dl>
      <div
        className="mt-3 pt-3 flex items-center justify-between border-t"
        style={{ borderColor: 'var(--color-border)' }}
      >
        <span className="text-sm font-semibold" style={{ color: 'var(--color-text-default)' }}>
          {totalLabel}
        </span>
        <span className="text-lg font-bold" style={{ color: 'var(--color-text-default)' }}>
          {total}
        </span>
      </div>
      {footer && <div className="mt-3">{footer}</div>}
    </aside>
  );
}

/* ── Tabs ─────────────────────────────────────────────────────────────────
 * Underline tab bar. Active tab uses the brand action color underline.
 */
export function Tabs({ tabs = [], value, onChange, className }) {
  return (
    <div
      role="tablist"
      className={clsx('flex items-center gap-6 border-b', className)}
      style={{ borderColor: 'var(--color-border)' }}
    >
      {tabs.map((t) => {
        const active = t.value === value;
        return (
          <button
            key={t.value}
            role="tab"
            aria-selected={active}
            onClick={() => onChange?.(t.value)}
            className="relative -mb-px py-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-action-primary)] rounded-sm"
            style={{ color: active ? 'var(--color-text-default)' : 'var(--color-text-muted)' }}
          >
            {t.label}
            {active && (
              <span
                className="absolute left-0 right-0 -bottom-px h-0.5"
                style={{ background: 'var(--color-action-primary)' }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

/* ── Drawer ───────────────────────────────────────────────────────────────
 * Lightweight right-side drawer scaffold. (Iter-13 may consolidate onto
 * Slideover; kept minimal + overlay-shadowed here as a showcase primitive.)
 */
export function Drawer({ open, onClose, title, children, width = 360 }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40" role="dialog" aria-modal="true">
      <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.4)' }} onClick={onClose} />
      <div
        className="absolute right-0 top-0 h-full p-4 overflow-y-auto"
        style={{
          width,
          background: 'var(--color-surface)',
          boxShadow: 'var(--shadow-overlay, 0 10px 30px rgba(0,0,0,0.2))',
        }}
      >
        {title && (
          <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--color-text-default)' }}>
            {title}
          </h3>
        )}
        {children}
      </div>
    </div>
  );
}

/* ── Charts (BarChart) ──────────────────────────────────────────────────────
 * Dependency-free SVG bar chart wrapper — enough to render Reports KPIs in
 * Iter-13 without pulling a charting lib yet. Brand-action bars on a flat card.
 */
export function BarChart({ data = [], height = 160, valueFormat = (v) => v, className }) {
  const max = Math.max(1, ...data.map((d) => d.value || 0));
  return (
    <div className={clsx('w-full', className)}>
      <div className="flex items-end gap-3" style={{ height }}>
        {data.map((d, i) => (
          <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1">
            <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
              {valueFormat(d.value)}
            </span>
            <div
              className="w-full rounded-t-[var(--radius-sm)]"
              style={{
                height: `${((d.value || 0) / max) * (height - 28)}px`,
                background: 'var(--color-action-primary)',
                minHeight: 2,
              }}
            />
            <span className="text-xs truncate w-full text-center" style={{ color: 'var(--color-text-muted)' }}>
              {d.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export const Charts = { BarChart };
